import * as React from 'react';
import { Text, CountrySelect, Checkbox } from '../components/forms';
import Page from '../components/Page';
import Section from '../components/Section';
import { ButtonStyle } from '../components/Button';
import { ActionBar, ActionBarStyle } from '../components/ActionBar';
import { Contract } from '../contract';
import { ErrorFlash, SuccessFlash } from '../components/Flash';
import { ProfileContext, ProfileManager } from '../profile';
import { useRequestAsync, getHeaders } from '../api/client';
import * as dateFormat from 'dateformat';

const DATE_FORMAT = 'fullDate';

const nth = (d: number) => {
  if (d > 3 && d < 21) return 'th';
  switch (d % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

const formatDayOfMonth = (val: string) => {
  const str = dateFormat(val, 'd');

  return `${str}${nth(parseInt(str, 10))}`;
};

const update = (context: ProfileManager) =>
  useRequestAsync((data: Contract) => ({
    url: data.id ? `/store/contracts/${data.id}` : '/store/contracts/new',
    method: data.id ? 'PUT' : 'POST',
    body: JSON.stringify(data),
    headers: getHeaders(context),
  }));

const setBillingInfoVvalue = (obj: Contract, onChange: Function) => (
  field: string,
  newValue: String,
) =>
  onChange(
    Object.assign({}, obj, {
      billing_info: {
        ...obj.billing_info,
        [field]: newValue,
      },
    }),
  );

const getContract = (context: ProfileManager) => {
  if (context.profile.company.contract) {
    return context.profile.company.contract;
  }

  return {
    id: null,
    company_id: context.profile.company_id,
    company_name: context.profile.company.name,
    type: 'sale_percentage',
    trial_days: 0,
    product_limit: 0,
    percentage: 25,
    amount: 0,
    amount_currency: 'GBP',
    notes: '',
    is_active: true,
    is_in_trial: false,
    billing_info: {
      account_holder: context.profile.company.name,
      account_number: '',
      address_city: context.profile.company.location.city,
      address_country: context.profile.company.location.country,
      address_county: context.profile.company.location.county,
      address_line_1: context.profile.company.location.address_line_1,
      address_line_2: context.profile.company.location.address_line_2,
      address_postal_code: context.profile.company.location.postal_code,
      bank_address: '',
      bank_name: '',
      routing_number: '',
      swift_number: '',
    },
    started_at: new Date(),
    created_at: null,
    updated_at: null,
  };
};

export default function EditBillingInfo() {
  const context = React.useContext(ProfileContext);
  const hasContract = Object.keys(context.profile.company).includes(
    'contract_id',
  );
  const [contract, setContract] = React.useState(getContract(context));
  const [readTerms, setReadTerms] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [state, submit] = update(context);

  const handleFormSubmit = () => {
    window.scrollTo(0, 0);

    if (!readTerms) {
      setError(new Error('Please agree to the terms and conditions'));
      return;
    }

    submit(contract);
  };

  React.useEffect(() => {
    // Refresh the profile context with newly updated contract
    if (state.complete && !state.error) {
      context.set(
        Object.assign({}, context.profile, {
          company: {
            ...context.profile.company,
            contract_id: state.data.id,
            contract: state.data,
          },
        }),
      );
    }
  }, [state]);

  return (
    <Page
      title={hasContract ? 'Billing' : "Let's get you set up"}
      subtitle="Get paid out for products you sell on the Filtru app"
    >
      {state.error && <ErrorFlash error={state.error} />}
      {error && <ErrorFlash error={error} />}
      {state.complete && state.data && (
        <SuccessFlash text="Billing info has been saved. Please update your products to publish them in the app" />
      )}
      <form onSubmit={handleFormSubmit} id="contract">
        <Section title="Billing Address">
          <div className="flex flex-column flex-row-ns">
            <div className="w-100 w-50-ns pr2-ns">
              <Text
                label="Address Line 1"
                field={['address_line_1']}
                id="address_line_1"
                defaultValue={contract.billing_info.address_line_1}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
              <Text
                label="Address Line 2"
                field={['address_line_2']}
                id="address_line_2"
                defaultValue={contract.billing_info.address_line_2}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
              <Text
                label="Postal Code"
                field={['address_postal_code']}
                id="address_postal_code"
                defaultValue={contract.billing_info.address_postal_code}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
            </div>
            <div className="w-100 w-50-ns pl2-ns">
              <Text
                label="City"
                field={['address_city']}
                id="address_city"
                defaultValue={contract.billing_info.address_city}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
              <Text
                label="State"
                field={['address_county']}
                id="address_county"
                defaultValue={contract.billing_info.address_county}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
              <CountrySelect
                label="Country"
                field={['address_country']}
                id="address_country"
                defaultValue={contract.billing_info.address_country}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
            </div>
          </div>
        </Section>
        <Section title="Bank Account">
          <div className="flex flex-column flex-row-ns">
            <div className="w-100 w-50-ns pr2-ns">
              <Text
                label="Account Holder"
                field={['account_holder']}
                id="account_holder"
                defaultValue={contract.billing_info.account_holder}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
              <Text
                label="IBAN/Account Number"
                field={['account_number']}
                id="account_number"
                defaultValue={contract.billing_info.account_number}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
              <Text
                label="Routing/ABA/Sort Code"
                field={['routing_number']}
                id="routing_number"
                defaultValue={contract.billing_info.routing_number}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
              <Text
                label="BIC/Swift Number"
                field={['swift_number']}
                id="swift_number"
                defaultValue={contract.billing_info.swift_number}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
            </div>
            <div className="w-100 w-50-ns pl2-ns">
              <Text
                label="Bank Name"
                field={['bank_name']}
                id="bank_name"
                defaultValue={contract.billing_info.bank_name}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
              <Text
                label="Bank Address"
                field={['bank_address']}
                id="bank_address"
                defaultValue={contract.billing_info.bank_address}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
            </div>
          </div>
        </Section>

        <Section title="Supplier Terms &amp; Conditions">
          <div className="bg-ui-white-0 pa3 pa4-l">
            <iframe
              className="w-100 bw b--black-10"
              style={{
                height: '45vh',
              }}
              src="https://filtru.coffee/supplier-terms.html"
            />
            <p className="lh-copy ma0 mt3 tr">
              <a
                className="link black underline"
                href="https://filtru.coffee/supplier-terms.html"
                target="_blank"
              >
                Open Terms &amp; Conditions in a new tab →
              </a>
            </p>
          </div>
        </Section>

        <Section title={`Contract Quotation for ${contract.company_name}`}>
          <div className="bg-ui-white-0 pa3 pa4-l">
            <p className="flex flex-column flex-row-ns justify-between lh-copy ma0 mb3">
              <span className="b">Commission to Filtru Coffee:</span>
              <span className="normal">{contract.percentage}% per sale</span>
            </p>
            <p className="flex flex-column flex-row-ns justify-between lh-copy mv3">
              <span className="b">Contract start date:</span>
              <span className="normal">
                {dateFormat(contract.started_at, DATE_FORMAT)}
              </span>
            </p>
            <p className="flex flex-column flex-row-ns justify-between lh-copy mv3">
              <span className="b">Payout date:</span>
              <span className="normal">
                {formatDayOfMonth(contract.started_at)} of the month
              </span>
            </p>
            <p className="flex flex-column flex-row-ns justify-between lh-copy ma0 mt3">
              <span className="b">Orders notifications email:</span>
              <span className="normal">
                {context.profile.company.contact_email}
              </span>
            </p>
          </div>
        </Section>

        <div className="mt3 mt4-l red b">
          <Checkbox
            label=""
            field={['read']}
            id="read"
            className="red b w2"
            defaultValue={readTerms}
            onValueChange={(_: any, val: boolean) => {
              setReadTerms(val);
              setError(null);
            }}
            tooltip={`Check here to indicate that you have read and agree to the Supplier Terms and Contract Quotation listed above`}
          />
        </div>

        <ActionBar
          className={ActionBarStyle.alignCenter}
          actions={[
            {
              className: ButtonStyle.OK,
              label: contract.id ? 'Save' : 'Confirm',
              handler: handleFormSubmit,
            },
          ]}
        />
      </form>
    </Page>
  );
}
